/**
 * ShoppingListItemRow — the collapsed + expandable row for one ShoppingListItem,
 * the heart of the Planning list. Named to the canonical
 * [§ ShoppingListItem → Row anatomy](../../../../docs/for-developers/ARCHITECTURE_CONTRACT.md#row-anatomy)
 * so the contract, the prototype, and `apps/web` share one vocabulary.
 *
 * Collapsed: `shoppingListItemThumb` · `itemName` · `quantityFlag` ·
 * `brandLine` (`brandStar` ★) · `storeLocationOfferSummary` · `removeButton` (✕).
 * The prototype hides the inline settings gear on the collapsed row
 * (`#s-list-plan .prefgear{display:none}`). The collapsed row is
 * tap-to-expand only — the `brandLine` is display-only (a tap bubbles to the
 * row and opens it); settings access lives in the expanded view (the size
 * control and the fine-tune link).
 * The row is complete and legible with ZERO offer data — the summary only
 * appears when offers resolve (the additive price layer): a single `summaryLabel`
 * (`On sale · N stores`, else `$X best`), or a wordless `availabilityDot` when
 * stores have resolved but carry no offer, or nothing while offers load. There
 * is NO expand caret — tapping the row opens it.
 *
 * Expanded (tap the row): a "How many" quantity stepper that writes item
 * quantity; and — when offers exist — an optional "Where to buy" store pick that
 * persists the chosen offer (writes `selectedRetailerOfferId`, invariant I6),
 * preferred-brand-first. With no offers it shows the calm "prices appear once
 * your stores are connected" forthcoming state — never a fabricated price.
 *
 * Inline ⚙ → Settings is URL-only: navigate to `/products/:id?from=list`
 * (the surface renders "‹ List"); the expanded size control adds
 * `&section=size` (which opens the Size bottom-sheet). This row never touches
 * the settings surface.
 *
 * All styles flow from tokens (CP5).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatItemMeasure,
  formatResolvedMeasurement,
  type ShoppingListItemWithDetails,
} from '@shopista/shared/types';
import { routes } from '../../routes/paths';
import { StoreLocationOffersLoader } from './StoreLocationOffersLoader';
import {
  storeLocationOfferSummary,
  matchOffersToItem,
  settingsUrl,
  type StoreDealEntry,
  type StoreLocationOffers,
} from './storeLocationOfferResolver';
import { WhereToBuyPicker } from './WhereToBuyPicker';
import { DealOverlay } from '../deals/DealOverlay';
import type { BadgeSignals } from '../deals/useBadgeSignals';
import { shoppingListItemThumb } from './shoppingListItemThumb';
import { addedAtRelative } from './addedAtByline';
import * as s from './planningModeStyles';

export interface ShoppingListItemRowProps {
  item: ShoppingListItemWithDetails;
  /** The household's selected stores (id + name) — what the Where picker lists. */
  selectedStores: { id: string; name: string | null }[];
  /**
   * T27.2.7.9.3 — the household's PREFERRED store id (`is_favorite`), pre-
   * highlighted in the Where picker while nothing is picked yet. Null when none.
   */
  preferredStoreId?: string | null;
  /** True once the selected-store list query has resolved (vs still loading). */
  storesResolved: boolean;
  /**
   * The household's real liked brand NAMES (normalized lowercase). Read ONCE at
   * the list level (TD#2) and passed down so the brand qualifier can flag a
   * genuine "· your brand" without a per-row preference query (T12.5.1).
   */
  likedBrandNames: ReadonlySet<string>;
  /**
   * T12.5.1 (C/E) — the household's full preference signal sets, read ONCE at the
   * list level, so each matched offer row renders the SAME stacked badge set as
   * the Deals surface.
   */
  badgeSignals: BadgeSignals;
  /**
   * T12.5.1 (E) — this item's HouseholdProduct's linked SystemProduct id(s), so an
   * offer matches by IDENTITY (the offer carries a resolved `systemproduct_id`),
   * not a loose name match.
   */
  itemSystemProductIds: ReadonlySet<string>;
  onSetQuantity: (item: ShoppingListItemWithDetails, quantity: number) => void;
  /** Save the per-trip note (`notes`) — same orchestrator path as quantity. */
  onSetNote: (item: ShoppingListItemWithDetails, notes: string) => void;
  /**
   * T27.2.7.9.3 — persist the where-to-buy pick. `(storeLocationId,
   * retailerOfferId)`: a store with a matching deal → both ids; a store with no
   * deal → `(storeId, null)`; re-tap to clear → `(null, null)`.
   */
  onPickStore: (
    item: ShoppingListItemWithDetails,
    storeLocationId: string | null,
    retailerOfferId: string | null,
  ) => void;
  /**
   * T27.2.7.9.6 — pin a flyer to the item (`deal`) or clear the pin (`null`).
   * `retailerOfferId` is the deal's store's matched offer id (or null); when
   * pinning, the handler sets the chosen store + offer + pin in ONE write so a
   * flyer always belongs to its store (.9.6 reopen fix 2).
   */
  onPinDeal: (
    item: ShoppingListItemWithDetails,
    deal: StoreDealEntry | null,
    retailerOfferId: string | null,
  ) => void;
  /**
   * T27.2.7.9.6 — "Buy regular here": clear the pinned flyer + the carried offer
   * while keeping the selected store, so the compact row prices nothing.
   */
  onRemoveOffer: (item: ShoppingListItemWithDetails) => void;
  /** Remove this item from the list (same orchestrator path as the legacy row). */
  onRemove: (item: ShoppingListItemWithDetails) => void;
}

export function ShoppingListItemRow({
  item,
  selectedStores,
  preferredStoreId,
  storesResolved,
  likedBrandNames,
  badgeSignals,
  itemSystemProductIds,
  onSetQuantity,
  onSetNote,
  onPickStore,
  onPinDeal,
  onRemoveOffer,
  onRemove,
}: ShoppingListItemRowProps): JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // The where-to-buy picker is revealed on TAP of the "Where" summary row
  // (prototype `.wval` → `toggleWherePick`); collapsed by default so the panel
  // is note + Where-summary + footer. The picker block itself (store list /
  // radios / prices / "Other brands") is unchanged — its redesign is .9.3.
  const [showPicker, setShowPicker] = useState(false);

  // The note is edited locally and committed on blur (or Enter) through the
  // orchestrator — the same write path as quantity. Keep a local draft so typing
  // is smooth; re-sync it when the persisted value changes underneath (e.g. an
  // edit on another tab) so the field never goes stale.
  const persistedNote = item.notes ?? '';
  const hasNote = persistedNote.trim() !== '';
  const [noteDraft, setNoteDraft] = useState(persistedNote);
  useEffect(() => {
    setNoteDraft(persistedNote);
  }, [persistedNote]);
  const commitNote = () => {
    const next = noteDraft.trim();
    if (next === persistedNote.trim()) return;
    onSetNote(item, next);
  };

  // The note textarea auto-grows with content up to the token cap (`maxHeight`
  // in `noteInputStyle`), then scrolls. Reset to `auto` first so the field can
  // shrink as well as grow, then size to the content (`scrollHeight`). Driven on
  // every input and whenever the field (re)mounts or the persisted note loads,
  // so an existing multi-line note shows expanded rather than clipped.
  const noteRef = useRef<HTMLTextAreaElement | null>(null);
  const autoSizeNote = useCallback(() => {
    const el = noteRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);
  useEffect(() => {
    if (open) autoSizeNote();
  }, [open, noteDraft, autoSizeNote]);

  // Aggregate each selected store's resolved offers, keyed by store id. The
  // loaders below push into this map; the summary + availability derive from it.
  const [storeLocationOffers, setStoreLocationOffers] = useState<Record<string, StoreLocationOffers>>(
    {},
  );
  const handleResolved = useCallback((result: StoreLocationOffers) => {
    setStoreLocationOffers((prev) => {
      const existing = prev[result.storeId];
      if (
        existing &&
        existing.loading === result.loading &&
        existing.offers === result.offers
      ) {
        return prev;
      }
      return { ...prev, [result.storeId]: result };
    });
  }, []);

  const stores = useMemo(
    () => Object.values(storeLocationOffers),
    [storeLocationOffers],
  );
  const matched = useMemo(
    () =>
      matchOffersToItem(
        item,
        stores,
        likedBrandNames,
        itemSystemProductIds,
        badgeSignals,
      ),
    [item, stores, likedBrandNames, itemSystemProductIds, badgeSignals],
  );

  const hasSelectedStores = selectedStores.length > 0;
  const storesAllResolved =
    storesResolved &&
    selectedStores.length > 0 &&
    selectedStores.every((st) => {
      const r = storeLocationOffers[st.id];
      return r != null && !r.loading;
    });

  const summary = storeLocationOfferSummary(matched, {
    hasSelectedStores,
    storesResolved: storesAllResolved,
  });

  // The where-to-buy SUMMARY line (prototype `.wval`): the CHOSEN store's name
  // (read store-first from the server-JOINed `display_store` / `store_name`),
  // else "Select store". A store can be picked with no deal, so this reads the
  // store anchor — not the offer. Tapping the summary reveals the picker below.
  const whereSummary =
    item.selected_storelocation_id != null
      ? item.display_store ?? item.store_name ?? 'Your store'
      : 'Select store';

  const qty = Number(item.quantity) || 1;
  // quantityFlag text — the prototype shows `· 1 bottle` (a count noun) when one
  // is set, else a bare `· ×N` multiplier. The count noun is the per-trip
  // `quantity_unit` only; the resolved SIZE (`4 L`) lives ONLY in the size badge
  // below, never duplicated here (T36.3 — dropped the size-unit fallback that
  // rendered `· 1 L` beside the `4 L` badge).
  const measureNoun = formatItemMeasure({
    quantity_unit: item.quantity_unit ?? null,
  });
  const qtyFlagText = measureNoun ? `· ${qty} ${measureNoun}` : `· ×${qty}`;
  const brand = item.offer_brand ?? null;

  // T12.1.8.7 — a flyer-added item carries the openable deal reference. While
  // the deal lives (`flyerdeal_id` set), expose a "View deal" pill.
  // T27.2.7.9.4 — tapping it opens the deal IN PLACE (an overlay over the list,
  // the same sheet the Deals page uses), not a navigation to /deals. Close
  // returns to the item. On the deal's purge the FK nulls, so we omit the
  // control rather than show a dead link.
  // T27.2.7.9.6 — the overlay also opens an arbitrary flyer the Where to buy
  // picker hands up (the "View" affordance on a browse row), so the open deal id
  // is tracked in state (defaulting to the item's pinned `flyerdeal_id` for the
  // "View deal" pill) rather than hardcoded to `item.flyerdeal_id`.
  const dealId = item.flyerdeal_id ?? null;
  const [overlayDealId, setOverlayDealId] = useState<string | null>(null);
  const openDeal = () => {
    if (!dealId) return;
    setOverlayDealId(dealId);
  };
  // T27.2.7.9.6 — open a specific flyer (a picker browse row's "View").
  const openDealById = (flyerDealId: string) => setOverlayDealId(flyerDealId);
  // T36.3 — the product's size badge renders through the ONE shared formatter from
  // the resolved measurement snapshot (`4 L`, `12 × 20 g`). No fabricated unit:
  // null when the product has no resolved measurement.
  const size = formatResolvedMeasurement({
    measurement_pack_count: item.product_measurement_pack_count ?? null,
    measurement_value: item.product_measurement_value ?? null,
    measurement_unit: item.product_measurement_unit ?? null,
    measurement_dimension: item.product_measurement_dimension ?? null,
    measurement_forms: item.product_measurement_forms ?? null,
  });

  // addedByByline — who added the item. `added_by_name` is the resolved display
  // name; fall back to a generic label when only the id is present, and omit the
  // byline entirely when neither is set (an item with no recorded adder).
  const addedByName = item.added_by_name?.trim() || null;
  const hasAddedBy = addedByName != null || item.added_by != null;
  const addedByText = addedByName ? `Added by ${addedByName}` : 'Added by you';
  // Relative "when added" suffix (today / yesterday / N days ago). Omitted when
  // `created_at` is missing or unparseable — never render `· Invalid Date`.
  const addedAt = addedAtRelative(item.created_at);

  const goSettings = (section?: 'brands' | 'size') => {
    navigate(
      settingsUrl(
        routes.products,
        item.householdproduct_id,
        section ? { section } : undefined,
      ),
    );
  };

  // The single `summaryLabel`'s tone: the sale label reads danger-tinted, the
  // best-price label success-tinted, anything else the quiet neutral.
  const summaryLabelStyle =
    summary?.kind === 'label' && summary.label.startsWith('On sale')
      ? s.summaryLabelSaleStyle
      : summary?.kind === 'label' && summary.label.endsWith('best')
        ? s.summaryLabelBestStyle
        : s.summaryLabelStyle;

  return (
    <div style={s.cardStyle} data-testid={`plan-item-${item.id}`}>
      {/* Always-mounted per-store loaders — render nothing, feed the summary. */}
      {selectedStores.map((st) => (
        <StoreLocationOffersLoader
          key={st.id}
          storeId={st.id}
          storeName={st.name}
          onResolved={handleResolved}
        />
      ))}

      <div style={s.rowHeaderStyle}>
      <button
        type="button"
        className="plan-row-focus"
        style={s.rowStyle}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`plan-item-${item.id}-row`}
      >
        {/* shoppingListItemThumb — the HouseholdProduct's emoji or photo. */}
        <span style={s.thumbStyle} aria-hidden="true">
          {shoppingListItemThumb(item)}
        </span>
        <span style={s.metaStyle}>
          <span style={s.nameStyle}>
            {/* itemName · quantityFlag */}
            <span style={s.titleTextStyle}>{item.display_title}</span>
            <span style={s.qtyFlagStyle}>{qtyFlagText}</span>
            {/* notedot — a small dot when a note exists (deliverable 3, both
                lists). The note is authored in the expanded view. */}
            {hasNote && (
              <span
                style={s.noteDotStyle}
                aria-label="Has a note"
                data-testid={`plan-item-${item.id}-notedot`}
              />
            )}
            {/* The prototype hides the inline settings gear on the collapsed
                row (`#s-list-plan .prefgear{display:none}`) — edits live in the
                expand panel and via the brandLine tap. */}
          </span>
          {(brand || size) && (
            // brandLine (with brandStar ★) — display-only: the chosen Brand +
            // package size. NOT interactive — a tap bubbles to the row button
            // and expands the row (the collapsed row is tap-to-expand only).
            // Settings access for brand/size lives in the expanded view.
            <span
              style={s.brandLineStyle}
              data-testid={`plan-item-${item.id}-brand`}
            >
              {/* The brandStar ★ denotes a preferred BRAND. With only a size and
                  no brand, the star would misread as a brand flag, so it appears
                  only when a brand is actually set. */}
              {brand && (
                <span style={s.starStyle} aria-hidden="true">
                  ★
                </span>
              )}
              {[brand, size].filter(Boolean).join(' · ')}
            </span>
          )}
          {dealId && (
            <div>
              <span
                role="button"
                tabIndex={0}
                style={s.dealLinkStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  openDeal();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                    openDeal();
                  }
                }}
                aria-label={`View the flyer deal for ${item.display_title}`}
                data-testid={`plan-item-${item.id}-view-deal`}
              >
                🏷️ View deal
              </span>
            </div>
          )}
        </span>
        {/* storeLocationOfferSummary — at most one of: a summaryLabel, or a
            wordless availabilityDot. Nothing renders while offers load. No
            expand caret — the row itself taps to open. */}
        <span style={s.statusColStyle}>
          {summary?.kind === 'label' && (
            <span
              style={summaryLabelStyle}
              data-testid={`plan-item-${item.id}-summary`}
            >
              {summary.label}
            </span>
          )}
          {summary?.kind === 'dot' && (
            <span
              style={s.availabilityDotStyle}
              aria-label="Not at your selected stores"
              data-testid={`plan-item-${item.id}-availability-dot`}
            />
          )}
        </span>
      </button>
        {/* removeButton (✕) — removes the item from the list. */}
        <button
          type="button"
          style={s.removeStyle}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item);
          }}
          aria-label={`Remove ${item.display_title} from list`}
          data-testid={`plan-item-${item.id}-remove`}
        >
          ✕
        </button>
      </div>

      {open && (
        <div
          style={s.expandedViewStyle}
          data-testid={`plan-item-${item.id}-expanded`}
        >
          {/* How many — always present, the planning-first control. */}
          <div style={s.qtyRowStyle}>
            <span style={s.qtyLabelStyle}>How many</span>
            {/* Stepper + size link share one horizontal row so they never wrap
                apart on narrow / large-font devices; the "How many" label stacks
                above (qtyRowStyle is a column). */}
            <div style={s.stepperRowStyle}>
              <span style={s.stepperStyle}>
                <button
                  type="button"
                  style={s.stepperButtonStyle}
                  onClick={() => onSetQuantity(item, Math.max(1, qty - 1))}
                  aria-label="Decrease quantity"
                  data-testid={`plan-item-${item.id}-qty-dec`}
                >
                  −
                </button>
                <span
                  style={s.stepperValueStyle}
                  data-testid={`plan-item-${item.id}-qty-value`}
                >
                  {qty}
                </span>
                <button
                  type="button"
                  style={s.stepperButtonStyle}
                  onClick={() => onSetQuantity(item, qty + 1)}
                  aria-label="Increase quantity"
                  data-testid={`plan-item-${item.id}-qty-inc`}
                >
                  +
                </button>
              </span>
              {/* sizeLink — ALWAYS shown in the expanded view (per the prototype
                  `.unit` at line 905 / the contract § Row anatomy → Expanded).
                  Reads `· {size} ›` when a package size is set (e.g. `· 500 mL ›`),
                  else the calm `· Set size ›` prompt. Tappable → HouseholdProduct
                  settings (`from=list&section=size`), which opens the Size
                  bottom-sheet directly. Collapsed view shows no size affordance. */}
              <button
                type="button"
                style={s.unitStyle}
                onClick={() => goSettings('size')}
                data-testid={`plan-item-${item.id}-size`}
              >
                {`· ${size ?? 'Set size'} ›`}
              </button>
            </div>
          </div>

          {/* itemNote — a BARE editable field (no "Note" label, per the
              prototype's `.sd-note`). Planning authors it; saves `notes` on blur
              / Enter through the same orchestrator path as quantity. Read-only in
              Shopping (a different render). */}
          <div style={s.noteRowStyle}>
            <textarea
              id={`plan-item-${item.id}-note-input`}
              ref={noteRef}
              style={s.noteInputStyle}
              value={noteDraft}
              onChange={(e) => {
                setNoteDraft(e.target.value);
                autoSizeNote();
              }}
              onBlur={commitNote}
              onKeyDown={(e) => {
                // Enter commits (without a newline); Shift+Enter inserts one.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  commitNote();
                  e.currentTarget.blur();
                }
              }}
              rows={1}
              placeholder="e.g. only if it's on sale"
              data-testid={`plan-item-${item.id}-note`}
            />
          </div>

          {/* Where-to-buy SUMMARY row (prototype `.sd-row` `Select store ▾`):
              the picked store, else "Select store". Tapping it reveals the
              existing picker below — the picker (store list / radios / prices /
              "Other brands") is NOT rendered inline by default; its redesign is
              .9.3. */}
          <button
            type="button"
            style={s.whereSummaryRowStyle}
            onClick={() => setShowPicker((v) => !v)}
            aria-expanded={showPicker}
            data-testid={`plan-item-${item.id}-where`}
          >
            <span style={s.whereSummaryLabelStyle}>Where</span>
            <span style={s.whereSummaryValueStyle}>
              {whereSummary}
              <span style={s.whereSummaryCaretStyle} aria-hidden="true">
                ▾
              </span>
            </span>
          </button>

          {/* T27.2.7.9.3 — the ONE shared Where-to-buy picker (also used by
              Shopping's CheckRow). Lists the household's selected stores; the
              deal rides along when the chosen store has a matching offer. */}
          {showPicker && (
            <WhereToBuyPicker
              item={item}
              selectedStores={selectedStores}
              preferredStoreId={preferredStoreId}
              storeLocationOffersById={storeLocationOffers}
              storesResolved={storesAllResolved}
              likedBrandNames={likedBrandNames}
              itemSystemProductIds={itemSystemProductIds}
              onPick={(storeId, offerId) => onPickStore(item, storeId, offerId)}
              onPin={(deal, offerId) => onPinDeal(item, deal, offerId)}
              onRemoveOffer={() => onRemoveOffer(item)}
              onOpenDeal={openDealById}
              testidPrefix={`plan-item-${item.id}`}
            />
          )}

          {/* itemFoot — the who-added byline on the left, the fine-tune link on
              the right. Planning shows both; the byline omits when no adder is
              recorded. */}
          <div style={s.itemFootStyle} data-testid={`plan-item-${item.id}-foot`}>
            {hasAddedBy && (
              <span
                style={s.addedByBylineStyle}
                data-testid={`plan-item-${item.id}-added-by`}
              >
                {addedAt ? `${addedByText} · ${addedAt}` : addedByText}
              </span>
            )}
            <button
              type="button"
              style={s.fineTuneLinkStyle}
              onClick={() => goSettings()}
              data-testid={`plan-item-${item.id}-fine-tune`}
            >
              ⚙ Fine-tune ›
            </button>
          </div>
        </div>
      )}

      {/* T27.2.7.9.4 — the in-place deal overlay (the same Deals-page sheet),
          mounted over the list. Opens the exact deal; closes back to the item.
          T27.2.7.9.6 — `overlayDealId` is the item's pinned deal (the "View deal"
          pill) OR a flyer the picker's "View" handed up. */}
      {overlayDealId && (
        <DealOverlay
          flyerDealId={overlayDealId}
          onClose={() => setOverlayDealId(null)}
        />
      )}
    </div>
  );
}
